import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Assessment as AssessmentIcon,
  SupervisorAccount as ManagerIcon
} from '@mui/icons-material';
import { CommissionTrackerService } from '../../services';

const CommissionReports = () => {
  const [selectedMonth, setSelectedMonth] = useState('');
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize with current month
  useEffect(() => {
    const currentMonth = CommissionTrackerService.getCurrentMonth();
    setSelectedMonth(currentMonth);
  }, []);

  // Load monthly summary when month changes
  useEffect(() => {
    if (selectedMonth) {
      loadMonthlySummary(selectedMonth);
    }
  }, [selectedMonth]);

  const loadMonthlySummary = async (month) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await CommissionTrackerService.getMonthlySummary(month);
      setMonthlySummary(response.data);
    } catch (err) {
      setError(err.message);
      setMonthlySummary(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => CommissionTrackerService.formatCurrency(amount || 0);
  const formatPercentage = (value) => `${(value || 0).toFixed(1)}%`;

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Month Selector */}
      <Box sx={{ 
        mb: 4, 
        display: 'flex', 
        justifyContent: 'flex-end', 
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2
      }}>
        <FormControl 
          sx={{ 
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              height: 48,
              backgroundColor: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#40a9ff'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1890ff',
                borderWidth: 2
              }
            }
          }}
        >
          <InputLabel>Select Month</InputLabel>
          <Select
            value={selectedMonth}
            label="Select Month"
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {CommissionTrackerService.getRecentMonths(24).map(month => (
              <MenuItem key={month} value={month}>
                {month}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(255, 77, 79, 0.1)',
            border: '1px solid rgba(255, 77, 79, 0.2)',
            '& .MuiAlert-icon': {
              fontSize: '1.5rem'
            }
          }}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          backgroundColor: 'white',
          borderRadius: 4,
          boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
        }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Loading commission data...
          </Typography>
        </Box>
      ) : monthlySummary ? (
        <>
          {/* Overall Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{
                height: '100%',
                borderRadius: 4,
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.04)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AssessmentIcon sx={{ color: 'primary.main', fontSize: '2.5rem', mr: 2 }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography 
                        color="textSecondary" 
                        variant="body2" 
                        sx={{ fontWeight: 500, fontSize: '0.875rem', mb: 0.5 }}
                      >
                        Total Deals
                      </Typography>
                      <Typography 
                        variant="h4"
                        sx={{ 
                          fontWeight: 700, 
                          color: 'text.primary',
                          fontSize: '2rem'
                        }}
                      >
                        {monthlySummary.totalDeals}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{
                height: '100%',
                borderRadius: 4,
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.04)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <MoneyIcon sx={{ color: 'success.main', fontSize: '2.5rem', mr: 2 }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography 
                        color="textSecondary" 
                        variant="body2" 
                        sx={{ fontWeight: 500, fontSize: '0.875rem', mb: 0.5 }}
                      >
                        Total Commission
                      </Typography>
                      <Typography 
                        variant="h4"
                        sx={{ 
                          fontWeight: 700, 
                          color: 'success.dark',
                          fontSize: '2rem'
                        }}
                      >
                        {formatCurrency(monthlySummary.totalCommission)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{
                height: '100%',
                borderRadius: 4,
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.04)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUpIcon sx={{ color: 'info.main', fontSize: '2.5rem', mr: 2 }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography 
                        color="textSecondary" 
                        variant="body2" 
                        sx={{ fontWeight: 500, fontSize: '0.875rem', mb: 0.5 }}
                      >
                        Rep Commission
                      </Typography>
                      <Typography 
                        variant="h4"
                        sx={{ 
                          fontWeight: 700, 
                          color: 'info.dark',
                          fontSize: '2rem'
                        }}
                      >
                        {formatCurrency(monthlySummary.totalRepCommission)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Status Breakdown */}
          <Paper sx={{ 
            p: 4, 
            mb: 4,
            borderRadius: 4,
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.04)'
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3,
                fontWeight: 600,
                color: '#1a1a1a',
                fontSize: '1.25rem'
              }}
            >
              Commission Status Breakdown
            </Typography>
            <Grid container spacing={3}>
              {Object.entries(monthlySummary.statusBreakdown || {}).map(([status, count]) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={status}>
                  <Card sx={{
                    borderRadius: 3,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                    border: '1px solid rgba(0,0,0,0.04)',
                    transition: 'all 0.2s ease',
                    aspectRatio: '1 / 1',
                    width: '100%',
                    minHeight: 160,
                    '&:hover': {
                      boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
                      transform: 'translateY(-1px)'
                    }
                  }}>
                    <CardContent sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textAlign: 'center',
                      height: '100%',
                      p: 3
                    }}>
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          color: 'primary.main',
                          fontWeight: 700,
                          mb: 1.5,
                          fontSize: { xs: '1.75rem', sm: '2rem' }
                        }}
                      >
                        {count}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: 'text.primary',
                          textTransform: 'capitalize',
                          fontWeight: 500,
                          mb: 1,
                          fontSize: { xs: '0.9rem', sm: '1rem' },
                          lineHeight: 1.3
                        }}
                      >
                        {status}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.secondary',
                          fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          fontWeight: 500
                        }}
                      >
                        {formatPercentage((count / monthlySummary.totalDeals) * 100)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Rep Performance Table */}
          <Paper sx={{ 
            mb: 4,
            borderRadius: 4,
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.04)',
            overflow: 'hidden'
          }}>
            <Box sx={{ 
              p: 3, 
              borderBottom: 1, 
              borderColor: 'divider',
              backgroundColor: '#fafafa'
            }}>
              <Typography 
                variant="h6"
                sx={{ 
                  fontWeight: 600,
                  color: '#1a1a1a',
                  fontSize: '1.25rem'
                }}
              >
                Individual Rep Performance
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>ISR Name</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#1a1a1a' }}>Deals Won</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#1a1a1a' }}>Total Commission</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#1a1a1a' }}>Avg Commission</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#1a1a1a' }}>% of Total Deals</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(monthlySummary.repSummaries || [])
                    .sort((a, b) => b.dealsWon - a.dealsWon)
                    .map((rep) => (
                      <TableRow 
                        key={rep.repName}
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                          '&:hover': { backgroundColor: '#f0f7ff' },
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography sx={{ fontWeight: 500 }}>
                              {rep.repName}
                            </Typography>
                            {rep.repName === 'Tyson Queen' && (
                              <Chip 
                                label="Manager" 
                                size="small" 
                                sx={{
                                  ml: 1,
                                  backgroundColor: 'secondary.main',
                                  color: 'white',
                                  fontWeight: 500,
                                  fontSize: '0.75rem'
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600,
                              color: 'text.primary'
                            }}
                          >
                            {rep.dealsWon}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600,
                              color: 'success.main'
                            }}
                          >
                            {formatCurrency(rep.totalCommission)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {formatCurrency(rep.averageCommission || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {formatPercentage((rep.dealsWon / monthlySummary.totalDeals) * 100)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Manager Summary (Special handling for Tyson Queen) */}
          {monthlySummary.managerSummaries && monthlySummary.managerSummaries.length > 0 && (
            <Paper sx={{
              borderRadius: 4,
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.04)',
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                p: 3, 
                borderBottom: 1, 
                borderColor: 'divider',
                backgroundColor: '#fafafa'
              }}>
                <Typography 
                  variant="h6"
                  sx={{ 
                    fontWeight: 600,
                    color: '#1a1a1a',
                    fontSize: '1.25rem'
                  }}
                >
                  Manager Commission Summary
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Manager</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#1a1a1a' }}>Total Deals Managed</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#1a1a1a' }}>Manager Commission</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#1a1a1a' }}>Own Rep Commission</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#1a1a1a' }}>Total Manager Compensation</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {monthlySummary.managerSummaries.map((manager) => {
                      const ownRepCommission = monthlySummary.repSummaries?.find(
                        rep => rep.repName === manager.managerName
                      )?.totalCommission || 0;

                      return (
                        <TableRow 
                          key={manager.managerName}
                          sx={{
                            '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                            '&:hover': { backgroundColor: '#f0f7ff' },
                            transition: 'background-color 0.2s ease'
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <ManagerIcon sx={{ 
                                mr: 1.5, 
                                color: 'secondary.main',
                                fontSize: '1.25rem'
                              }} />
                              <Typography sx={{ fontWeight: 500 }}>
                                {manager.managerName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 600,
                                color: 'text.primary'
                              }}
                            >
                              {manager.totalDealsManaged}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'info.main',
                                fontWeight: 500
                              }}
                            >
                              {formatCurrency(manager.managerCommission)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'success.main',
                                fontWeight: 500
                              }}
                            >
                              {formatCurrency(ownRepCommission)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 600,
                                color: 'primary.main'
                              }}
                            >
                              {formatCurrency(manager.totalManagerCommission || (manager.managerCommission + ownRepCommission))}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      ) : selectedMonth && !loading && (
        <Alert 
          severity="info"
          sx={{
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(33, 150, 243, 0.1)',
            border: '1px solid rgba(33, 150, 243, 0.2)',
            backgroundColor: '#f0f7ff',
            '& .MuiAlert-icon': {
              fontSize: '1.5rem'
            },
            '& .MuiAlert-message': {
              fontSize: '1rem',
              fontWeight: 500
            }
          }}
        >
          No commission data found for {selectedMonth}. Try selecting a different month or check if commission records have been generated.
        </Alert>
      )}
    </Box>
  );
};

export default CommissionReports;
